import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { BeforeSendFn, CaptureResult, PostHogConfig } from 'posthog-js';
import type { FlagEvaluation } from 'types/backend';
import { useAuth } from 'providers';
import {
  getPathNameFromEvent,
  isPHIRoute,
  sanitizePathname,
  sanitizeProperties,
  sanitizeUrl,
} from './analytics.utils';
import { useFlagEvaluation, useFlagEvaluationQuery } from './useFlagEvaluation';

const beforeSend: BeforeSendFn = (event: CaptureResult | null): CaptureResult | null => {
  if (!event || !event?.properties) {
    return null;
  }

  const pathname = getPathNameFromEvent(event);

  if (isPHIRoute(pathname)) {
    return null;
  }

  if (event.event === '$autocapture') {
    if (event.properties.$elements_chain) {
      const hasNoAutocapture = (event.properties.$elements_chain as string)?.includes?.(
        'data-ph-no-autocapture'
      );
      if (hasNoAutocapture) {
        return null;
      }
    }
  }

  if (event.properties) {
    event.properties = sanitizeProperties(event.properties);
  }
  return event;
};

function getPostHogOptions(
  apiHost: string,
  featureFlags: FlagEvaluation | undefined
): Partial<PostHogConfig> {
  return {
    api_host: apiHost,
    disable_session_recording: false,
    autocapture: {
      dom_event_allowlist: ['click', 'submit', 'change'],
      css_selector_allowlist: [':not([data-ph-no-autocapture])'],
      element_attribute_ignorelist: ['aria-label', 'title', 'alt'],
    },
    mask_all_text: true,
    capture_pageview: false,
    before_send: beforeSend,
    session_recording: {
      maskAllInputs: true,
      maskTextClass: 'masked',
    },
    respect_dnt: true,
    bootstrap: featureFlags?.userId
      ? {
          distinctID: featureFlags.userId,
          isIdentifiedID: true,
          featureFlags: featureFlags.flags,
        }
      : undefined,
    advanced_disable_feature_flags: true,
  };
}

function PageViewTracker() {
  const posthog = usePostHog();
  const location = useLocation();

  useEffect(() => {
    if (!posthog) return;

    posthog.capture('$pageview', {
      $current_url: sanitizeUrl(window.location.href),
      pathname: sanitizePathname(location.pathname),
    });
  }, [posthog, location]);

  return null;
}

function IdentifyUser() {
  const posthog = usePostHog();
  const { userId, organizationId } = useFlagEvaluation();

  useEffect(() => {
    if (!posthog) return;

    if (userId) {
      posthog.identify(userId);
      if (organizationId) {
        posthog.group('organization', organizationId);
      } else {
        posthog.resetGroups();
      }
    } else {
      posthog.reset();
    }
  }, [posthog, userId, organizationId]);

  return null;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string;
  const apiHost = import.meta.env.VITE_POSTHOG_API_HOST as string;
  const { loading } = useAuth();
  const { data, isLoading } = useFlagEvaluationQuery();
  const [settled, setSettled] = useState(false);
  if (!loading && !isLoading && !settled) setSettled(true);

  if (!apiKey || !apiHost || !settled) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={apiKey} options={getPostHogOptions(apiHost, data)}>
      <PageViewTracker />
      <IdentifyUser />
      {children}
    </PostHogProvider>
  );
}
